import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import dayjs from "dayjs";
import log from "logbeacon";
import { useParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { wrapperResult } from "@api/osp";
import { getMinValueFromArray } from "../utils";
import { cleanNullishParams } from "../utils/cleanParams";
import type {
  GetRankingStockRequest,
  StockSortField as StockSortFieldType,
} from "@/src/types/api-params";
import { StockSortField as StockSortFieldEnum } from "@/src/features/api/openData";
import { useOspApiClientProvider } from "@hooks";
import { QUERY_KEYS } from "@config";

const PAGE_SIZE = 10;

export interface StockResult {
  [key: string]: any;
}

export interface StockDashboardResult {
  data: {
    data: StockResult[];
    total: number;
  };
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number) => void;
  };
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

export interface GetStockRankingRequest extends GetRankingStockRequest {
  enabled?: boolean;
  gcTime?: number;
  enrichEnable?: boolean;
  enrichWithTradingDate?: boolean;
  order?: "ASC" | "DESC";
  sortFiled?: StockSortFieldType;
  pageSize?: number;
  homeCategory?: string;
  enrichWithSparkLine?: boolean;
  enrichWithRating?: boolean;
  type?: "REPORT_360";
  trading_period?: string;
  themeKey?: string | null;
  themeIds?: string[];
  themeSlugs?: string[];
  needTheme?: boolean;
  params?: Record<string, any>;
}
/**
 * Stock Dashboard Hook
 *
 * 基于 useTokenSeekDashboard 实现，支持：
 * - 动态 theme 参数从 URL params 获取
 * - 翻页功能
 * - 可配置的 enrichment 选项
 *
 * @param params 配置参数
 * @returns Stock Dashboard 查询结果和翻页控制
 */
export const useStockDashboard = (
  params: GetStockRankingRequest = {},
): StockDashboardResult => {
  const {
    enabled = true,
    gcTime = 0,
    enrichEnable = true,
    enrichWithTradingDate = true,
    order,
    sortFiled = StockSortFieldEnum.PRICE_CHANGE_PERCENTAGE,
    homeCategory,
    enrichWithSparkLine = false,
    enrichWithRating = false,
    type,
    trading_period = "7d",
    // 只支持传一种
    themeKey, // 主题slug
    themeIds, // 主题ids
    themeSlugs, // 主题slugs
    needTheme = true,
    market,
    params: restParams = {},
  } = params || {};
  const { openDataApi } = useOspApiClientProvider();
  const urlParams = useParams();

  // theme 由外部通过 themeKey 传入，不再使用 getStockTabMap
  const category = homeCategory || urlParams.category;
  const theme = needTheme ? (themeKey ?? null) : null;
  const refetchTimer = useRef<NodeJS.Timeout | number | null>(null);
  const now = dayjs().valueOf();
  // 翻页状态管理
  const [currentPage, setCurrentPage] = useState(1);
  const lastCategoryRef = useRef(category);
  const pageLimit = params?.pageSize || PAGE_SIZE;
  const availablePage = lastCategoryRef.current !== category ? 1 : currentPage;
  // 当 category 变化时重置页码
  useEffect(() => {
    if (lastCategoryRef.current !== category) {
      setCurrentPage(1);
      lastCategoryRef.current = category;
    }
  }, [category]);
  // API 查询
  const result = useQuery({
    enabled: !!openDataApi && (!!theme || !!themeIds || !!type) && enabled,
    staleTime: 30 * 1000,
    gcTime,
    queryKey: [
      QUERY_KEYS.STOCK_DASHBOARD,
      "openData",
      "stocks",
      "ranking",
      {
        theme: theme || themeIds,
        offset: availablePage,
        ...params,
        limit: pageLimit,
      },
      currentPage,
    ],
    queryFn: async () => {
      if (!theme && !themeIds && !type) {
        return null;
      }

      const requestParams = cleanNullishParams({
        offset: (availablePage - 1) * pageLimit, // API 从0开始计数
        limit: pageLimit,
        // ...(theme && { theme }),
        type,
        market,
        "enrich.enable": enrichEnable,
        "enrich.with_trading_data": enrichWithTradingDate,
        "sort.order": order,
        "sort.field": sortFiled,
        "enrich.with_sparkline.enable": enrichWithSparkLine,
        "enrich.with_sparkline.interval": "1h",
        "enrich.with_rating": enrichWithRating,
        "enrich.with_sparkline.trading_period": trading_period,
        ...restParams,
        themes: theme ? [theme] : themeSlugs ? themeSlugs : undefined, // 主题slugs
        themeIds: themeIds ? themeIds : undefined, // 主题ids
      });

      log.debug("[文案]requestParams: ", requestParams);
      const data = await wrapperResult(
        openDataApi!.stocks.getRankingStocks(requestParams),
      );
      log.debug("[文案]data: ", data);

      return data;
    },
  });

  // 使用 useMemo 包装 marketData 以避免每次渲染都重新创建
  const marketData = useMemo(() => {
    return (result?.data?.rows || [])[0]?.trading_data || {};
  }, [result?.data?.rows]);

  const {
    regular_market_open_time,
    regular_market_close_time,
    post_market_close_time,
    pre_market_open_time,
    next_pre_market_open_time,
  } = marketData;

  const getNextUtcMidnight = (): number => {
    const utcNow = dayjs().utc();
    const nextUtcMidnight = utcNow.add(1, "day").startOf("day");
    return nextUtcMidnight.valueOf();
  };
  const nextUtcMidnight = getNextUtcMidnight();

  // 使用 useMemo 包装 timeList 以避免每次渲染都重新创建数组
  const timeList = useMemo(
    () => [
      // 增加下一天时间的UTC零时时间，更新数据
      nextUtcMidnight,
      regular_market_open_time,
      regular_market_close_time,
      post_market_close_time,
      pre_market_open_time,
      next_pre_market_open_time,
    ],
    [
      nextUtcMidnight,
      regular_market_open_time,
      regular_market_close_time,
      post_market_close_time,
      pre_market_open_time,
      next_pre_market_open_time,
    ],
  );

  const refetchData = useCallback(() => {
    if (refetchTimer.current) {
      clearTimeout(refetchTimer.current as NodeJS.Timeout);
    }
    refetchTimer.current = null;
    const validTimes = timeList.filter(
      (time): time is number => typeof time === "number" && time > now,
    );

    const minTime =
      validTimes.length > 0 ? getMinValueFromArray(validTimes) : null;

    // 计算timeout，确保是有效的setTimeout参数
    let timeout: number;
    // setTimeout最大值约为2147483647毫秒（约24.8天），超过则设为最大值
    const MAX_TIMEOUT = 2147483647;
    // 设置为10秒后,请求数据
    const timeDiff = minTime ? minTime - now + 10000 : MAX_TIMEOUT;

    timeout = Math.min(timeDiff, MAX_TIMEOUT);

    // 确保timeout至少为1毫秒，避免0或负值
    timeout = Math.max(timeout, 1);

    return minTime
      ? setTimeout(() => {
          result.refetch();
        }, timeout)
      : null;
  }, [timeList, now, marketData, result]);

  useEffect(() => {
    if (
      regular_market_open_time ||
      regular_market_close_time ||
      post_market_close_time ||
      pre_market_open_time ||
      next_pre_market_open_time
    ) {
      refetchTimer.current = refetchData();
    }

    return () => {
      if (refetchTimer.current) {
        clearTimeout(refetchTimer.current as NodeJS.Timeout);
      }
    };
  }, [
    next_pre_market_open_time,
    post_market_close_time,
    pre_market_open_time,
    refetchData,
    regular_market_close_time,
    regular_market_open_time,
  ]);

  // 翻页处理
  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 数据处理
  const selectData = useMemo(() => {
    return (result.data?.rows || []).map((row: any, index: number) => {
      return {
        ...row,
        key: `${row.symbol || row.name || "stock"}-${index}`,
      } as StockResult;
    });
  }, [result.data?.rows]);

  return {
    ...result,
    data: {
      data: selectData,
      total: result.data?.total || 0,
    },
    pagination: {
      current: availablePage,
      pageSize: pageLimit,
      total: result.data?.total || 0,
      onChange: onPageChange,
    },
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
  };
};
